import { useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import Select from 'react-select'
import { useAtomValue, useSetAtom } from 'jotai'

import * as lib from '../../lib'
import * as state from '../../states'
import { IOfferScope } from '../../types'
import { EOfferScopeCapType, EOfferScopeStatus } from '../../types/enum'
import { ISelectOption } from '../../types/ui'
import { OFFER_SCOPE_CAP_TYPE_OPTIONS, OFFER_SCOPE_STATUS_OPTIONS } from '../../constants'
import { useSearchParams } from 'react-router'
import { reactSelectStyles } from '../filters'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IOfferScopeEditModalProps = {
	item: IOfferScope | null;
	show: boolean;
	onClose: () => void;
	onSave: () => void;
}

export const OfferScopeEditModal = (
	props: IOfferScopeEditModalProps
) => {
	const { t } = useTranslation()
	const isThemeDark = useAtomValue(state.themeIsDarkAtom)
	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const [searchParams] = useSearchParams()
	const tokens = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null
	const group = lib.groupSearchParams(searchParams.get('group') ?? '')

	const { item, show, onClose, onSave } = props
	const OFFER_SCOPE_CAP_TYPE_OPTIONS_TRANS = OFFER_SCOPE_CAP_TYPE_OPTIONS(t)
	const OFFER_SCOPE_STATUS_OPTIONS_TRANS = OFFER_SCOPE_STATUS_OPTIONS(t)

	const [name, setName] = useState<string>(item?.name ?? '')
	const [cap, setCap] = useState<string>(Number.isInteger(item?.cap) ? `${item?.cap}` : '')
	const [capType, setCapType] = useState<ISelectOption<EOfferScopeCapType> | null>(() => {
		return OFFER_SCOPE_CAP_TYPE_OPTIONS_TRANS.find(
			(optionItem) => optionItem.value === item?.capType
		) ?? OFFER_SCOPE_CAP_TYPE_OPTIONS_TRANS[0]
	})
	const [status, setStatus] = useState<ISelectOption<EOfferScopeStatus> | null>(() => {
		return OFFER_SCOPE_STATUS_OPTIONS_TRANS.find(
			(optionItem) => optionItem.value === item?.status
		) ?? OFFER_SCOPE_STATUS_OPTIONS_TRANS[0]
	})

	if (!show) {
		return null
	}

	const onUpdate = async () => {
		const uri: string = `offer/scope/${item?.id}`
		const method: string = 'PATCH'
		const params: lib.FetchOptions['params'] = {}
		const headers = lib.bearer(access)
		const body: string = JSON.stringify({
			name,
			cap: +cap > 0 ? +cap : 0,
			capType: capType?.value,
			status: status?.value,
			...(item?.group ? { group: item?.group } : {})
		})
		const options: lib.FetchOptions = { body, method, params, headers }

		try {
			setFetchIsLoading(state.turnOnLoader)
			await lib.fetching<void>(uri, options)
			onSave()
		} catch (error: unknown) {
			// TODO: pretty notification alert
			console.error(error)
		} finally {
			setFetchIsLoading(state.turnOffLoader)
		}
	}

	const modalTitle = `${t(ALL_LANG_KEY.offer_scope_crud_modal.title_edit)} #${item?.id}`

	return <>
		<Modal show={show} onHide={onClose} className={'scroll'}>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<Form>
					<Form.Group>
						<Form.Label className="text-dark" htmlFor={'input-name'}>{`${t(ALL_LANG_KEY.offer_scope_crud_modal.field_name_label)}`}</Form.Label>
						<Form.Control
							id={'input-name'}
							placeholder={`${t(ALL_LANG_KEY.offer_scope_crud_modal.field_name_placeholder)}`}
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
						<br/>
						<Form.Label className="text-dark" htmlFor={'input-cap'}>{`${t(ALL_LANG_KEY.offer_scope_crud_modal.field_cap_label)}`}</Form.Label>
						<Form.Control
							id={'input-cap'}
							placeholder={`${t(ALL_LANG_KEY.offer_scope_crud_modal.field_cap_placeholder)}`}
							type="text"
							value={cap}
							onChange={(e) => setCap(e.target.value)}
						/>
						<br/>
						<Form.Label className="text-dark" htmlFor={'select-capType'}>{`${t(ALL_LANG_KEY.offer_scope_crud_modal.field_cap_type_label)}`}</Form.Label>
						<Select
							inputId={'select-capType'}
							className="react-select-container"
							options={OFFER_SCOPE_CAP_TYPE_OPTIONS_TRANS}
							value={capType}
							placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
							noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
							isSearchable={false}
							styles={reactSelectStyles<ISelectOption<EOfferScopeCapType>, false>(isThemeDark)}
							onChange={(selectedItem) => {
								setCapType(selectedItem)
							}}
						/>
						<br/>
						<Form.Label className="text-dark" htmlFor={'select-status'}>{`${t(ALL_LANG_KEY.offer_scope_crud_modal.field_status_label)}`}</Form.Label>
						<Select
							inputId={'select-status'}
							className="react-select-container"
							options={OFFER_SCOPE_STATUS_OPTIONS_TRANS}
							value={status}
							placeholder={`${t(ALL_LANG_KEY.general.react_select_placeholder)}`}
							noOptionsMessage={() => `${t(ALL_LANG_KEY.general.react_select_no_options)}`}
							isSearchable={false}
							styles={reactSelectStyles<ISelectOption<EOfferScopeStatus>, false>(isThemeDark)}
							onChange={(selectedItem) => {
								setStatus(selectedItem)
							}}
						/>
					</Form.Group>
				</Form>
			</Modal.Body>

			<Modal.Footer>
				<Button variant="primary" onClick={onUpdate}>{`${t(ALL_LANG_KEY.offer_scope_crud_modal.btn_update)}`}</Button>
				<Button variant="secondary" onClick={onClose}>{`${t(ALL_LANG_KEY.offer_scope_crud_modal.btn_close)}`}</Button>
			</Modal.Footer>
		</Modal>
	</>
}
